import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Icon from '../components/Icon';

const STAGE_COLORS = {
  new: '#D4FF3A',
  contacted: '#A78BFA',
  meeting_booked: '#F2B84B',
  proposal_sent: '#FF8A3D',
};

const STAGE_LABELS = {
  new: 'Yeni',
  contacted: 'İletişim',
  meeting_booked: 'Toplantı',
  proposal_sent: 'Teklif',
};

const ACTIVITY_ICONS = {
  note: 'note',
  call: 'phone',
  email: 'mail',
  meeting: 'handshake',
  task: 'clipboard',
  stage_change: 'refresh',
};

const TASK_STATUS = {
  todo: { label: 'Yapılacak', color: '#9A9A92' },
  in_progress: { label: 'Devam Ediyor', color: '#F2B84B' },
  done: { label: 'Tamamlandı', color: '#34D399' },
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await api.get('/dashboard/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Dashboard verileri yüklenemedi:', err);
    } finally {
      setLoading(false);
    }
  };

  // Yaklaşan görev satırından durum/tarih güncelle; tamamlanınca listeden düşer
  const updateFollowupTask = async (id, patch) => {
    try {
      await api.put(`/tasks/${id}`, patch);
      fetchStats();
    } catch (err) {
      console.error('Görev güncellenemedi:', err);
    }
  };

  const dueBadge = (date) => {
    if (!date) return { label: '—', color: '#9A9A92' };
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    const diff = Math.ceil((d - now) / 86400000);
    if (diff < 0) return { label: `Gecikmiş (${Math.abs(diff)} gün)`, color: '#FF6B5B' };
    if (diff === 0) return { label: 'Bugün', color: '#FF6B5B' };
    if (diff === 1) return { label: 'Yarın', color: '#F2B84B' };
    return { label: `${diff} gün kaldı`, color: '#9A9A92' };
  };

  if (loading) {
    return <div className="page-loading">Yükleniyor...</div>;
  }

  const pipeline = stats?.pipeline || [];
  const maxCount = Math.max(...pipeline.map(p => parseInt(p.count)), 1);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(val || 0);
  };

  // Toplantı zamanı: bugünse "Bugün 14:30", değilse "Paz 18.10 14:30"
  const meetingTime = (date) => {
    const d = new Date(date);
    const hm = d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
    const isToday = d.toDateString() === new Date().toDateString();
    const day = d.toLocaleDateString('tr-TR', { weekday: 'short' });
    const dm = `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`;
    return isToday ? `Bugün ${hm}` : `${day} ${dm} ${hm}`;
  };

  const timeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const d = new Date(date);
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return 'Az önce';
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
    return `${Math.floor(diff / 86400)} gün önce`;
  };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Genel bakış ve özetler</p>
        </div>
        <div className="page-actions">
          <button className="btn btn--primary" onClick={() => navigate('/leads')}>
            + Yeni Lead
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-card__header">
            <span className="stat-card__label">Toplam Lead</span>
            <span className="stat-card__icon"><Icon name="users" size={20} /></span>
          </div>
          <div className="stat-card__value">{stats?.leads_count || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card__header">
            <span className="stat-card__label">Aktif Hesaplar</span>
            <span className="stat-card__icon"><Icon name="building" size={20} /></span>
          </div>
          <div className="stat-card__value">{stats?.accounts_count || 0}</div>
        </div>
        {stats?.monthly_revenue != null && (
          <div className="stat-card">
          <div className="stat-card__header">
            <span className="stat-card__label">Aylık Gelir</span>
            <span className="stat-card__icon"><Icon name="cash" size={20} /></span>
          </div>
          <div className="stat-card__value">{formatCurrency(stats?.monthly_revenue)}</div>
        </div>
          )}
        <div className="stat-card">
          <div className="stat-card__header">
            <span className="stat-card__label">Açık Görevler</span>
            <span className="stat-card__icon"><Icon name="clipboard" size={20} /></span>
          </div>
          <div className="stat-card__value">{stats?.open_tasks_count || 0}</div>
        </div>
      </div>

      {/* Pipeline Chart */}
      <div className="card" style={{ marginBottom: 24 }}>
        <h3 className="card__title">Satış Pipeline</h3>
        <div className="pipeline-chart">
          {pipeline.map(p => (
            <div key={p.stage} className="pipeline-chart__bar-group">
              <div className="pipeline-chart__count">{p.count}</div>
              <div
                className="pipeline-chart__bar"
                style={{
                  height: (parseInt(p.count) / maxCount) * 80,
                  background: STAGE_COLORS[p.stage] || '#D4FF3A',
                }}
              />
              <div className="pipeline-chart__label">{STAGE_LABELS[p.stage] || p.stage}</div>
            </div>
          ))}
          {pipeline.length === 0 && (
            <p className="text-muted" style={{ textAlign: 'center', width: '100%' }}>Pipeline verisi yok</p>
          )}
        </div>
      </div>

      {/* Two columns */}
      <div className="dashboard-columns">
        <div className="card">
          <h3 className="card__title">Yaklaşan Görevler</h3>
          {(stats?.upcoming_followups || []).length > 0 ? (
            <div style={{ maxHeight: 360, overflowY: 'auto', margin: '0 -8px', padding: '0 8px' }}>
              {stats.upcoming_followups.map((t, i, arr) => {
                const d = dueBadge(t.due_date);
                return (
                  <div key={t.id} className={`list-item ${i < arr.length - 1 ? 'list-item--bordered' : ''}`} style={{ gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 150 }}>
                      <div style={{ fontWeight: 500 }}>{t.company ? `${t.company} — ` : ''}{t.title}</div>
                      <div className="text-sm" style={{ color: d.color }}>
                        {d.label}{t.assigned_name ? ` · ${t.assigned_name}` : ''}
                      </div>
                    </div>
                    <input
                      type="date"
                      className="form-input"
                      style={{ width: 'auto', padding: '4px 8px' }}
                      value={t.due_date ? String(t.due_date).slice(0, 10) : ''}
                      onChange={(e) => { if (e.target.value) updateFollowupTask(t.id, { due_date: e.target.value }); }}
                    />
                    <select
                      className="form-input"
                      style={{ width: 'auto', padding: '4px 8px', color: TASK_STATUS[t.status]?.color, fontWeight: 600 }}
                      value={t.status}
                      onChange={(e) => updateFollowupTask(t.id, { status: e.target.value })}
                    >
                      <option value="todo">Yapılacak</option>
                      <option value="in_progress">Devam Ediyor</option>
                      <option value="done">Tamamlandı</option>
                    </select>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-muted">Yaklaşan görev yok</p>
          )}
        </div>

        <div className="card">
          <h3 className="card__title">Son Aktiviteler</h3>
          {(stats?.recent_activities || []).length > 0 ? (
            stats.recent_activities.slice(0, 5).map((a, i) => (
              <div key={a.id} className={`list-item list-item--with-icon ${i < 4 ? 'list-item--bordered' : ''}`}>
                <span className="list-item__icon"><Icon name={ACTIVITY_ICONS[a.type] || 'pin'} /></span>
                <div className="list-item__content">
                  <div>{a.user_name ? `${a.user_name} — ` : ''}{a.description}</div>
                  <div className="text-muted text-sm">{timeAgo(a.created_at)}</div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-muted">Henüz aktivite yok</p>
          )}
        </div>
      </div>

      {/* Bu haftaki toplantılar — mevcut kartlara ek (Son Aktiviteler yerinde kalır) */}
      <div className="card" style={{ marginTop: 24 }}>
        <h3 className="card__title">Bugün ve Bu Haftaki Toplantılar</h3>
        {(stats?.week_meetings || []).length > 0 ? (
          <div style={{ maxHeight: 360, overflowY: 'auto', margin: '0 -8px', padding: '0 8px' }}>
            {stats.week_meetings.map((m, i, arr) => {
              const today = new Date(m.meeting_date).toDateString() === new Date().toDateString();
              return (
                <div key={m.id} className={`list-item list-item--with-icon ${i < arr.length - 1 ? 'list-item--bordered' : ''}`}>
                  <span className="list-item__icon" style={{ color: today ? 'var(--accent)' : undefined }}><Icon name={today ? 'clock' : 'calendar'} /></span>
                  <div className="list-item__content">
                    <div style={{ fontWeight: today ? 600 : 400 }}>{m.title}</div>
                    <div className="text-muted text-sm">
                      {meetingTime(m.meeting_date)}
                      {(m.account_brand || m.account_company || m.lead_company) ? ` · ${m.account_brand || m.account_company || m.lead_company}` : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted">Bu hafta toplantı yok</p>
        )}
      </div>
    </div>
  );
}
